import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import TransactionCard from "@/components/TransactionCard";
import ExceptionDialog from "@/components/ExceptionDialog";
import { useTransactions, Column } from "@/lib/useTransactions";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, PackageCheck, AlertTriangle } from "lucide-react";

const FD_EXCEPTIONS = ["Customer not present", "Customer temporarily left", "Payment issue",
  "Verification issue", "Package issue", "Customer query", "System issue",
  "Supervisor assistance required", "Transaction cancelled", "Other"];

export default function FrontDesk() {
  const { txns, reload } = useTransactions();
  const [excTxn, setExcTxn] = useState(null);
  const [deliverTxn, setDeliverTxn] = useState(null);
  const [busy, setBusy] = useState(null);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/transactions/${id}/${action}`);
      toast.success("Updated");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
      setDeliverTxn(null);
    }
  };

  const prepared = txns.filter((t) => t.current_status === "PREPARED");
  const inService = txns.filter((t) => t.current_status === "SERVICE_STARTED");
  const overdue = txns.filter((t) => t.is_overdue && ["PREPARED", "SERVICE_STARTED"].includes(t.current_status));
  const exceptions = txns.filter((t) => t.is_exception);

  return (
    <DashboardLayout title="Front Desk">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Column title="Prepared" count={prepared.length} accent="#22C55E" testId="col-prepared">
          {prepared.map((t) => (
            <TransactionCard key={t.id} txn={t} subtitle="Prepared">
              <Button size="sm" className="gap-1" disabled={busy === t.id} onClick={() => act(t.id, "start-service")} data-testid={`start-${t.kin_number}`}>
                <Play size={14} /> Start Service
              </Button>
              <Button size="sm" variant="outline" onClick={() => setExcTxn(t)}><AlertTriangle size={14} /></Button>
            </TransactionCard>
          ))}
        </Column>

        <Column title="In Service" count={inService.length} accent="#3B82F6" testId="col-inservice">
          {inService.map((t) => (
            <TransactionCard key={t.id} txn={t} assignedName={t.service_started_by_name} subtitle="Serving">
              <Button size="sm" className="gap-1" disabled={busy === t.id} onClick={() => setDeliverTxn(t)} data-testid={`deliver-${t.kin_number}`}>
                <PackageCheck size={14} /> Delivered
              </Button>
              <Button size="sm" variant="outline" onClick={() => setExcTxn(t)}><AlertTriangle size={14} /></Button>
            </TransactionCard>
          ))}
        </Column>

        <Column title="Overdue" count={overdue.length} accent="#FF3B30" testId="col-fd-overdue">
          {overdue.map((t) => <TransactionCard key={t.id} txn={t} assignedName={t.service_started_by_name} />)}
        </Column>

        <Column title="Exceptions" count={exceptions.length} accent="#F59E0B" testId="col-fd-exceptions">
          {exceptions.map((t) => <TransactionCard key={t.id} txn={t} />)}
        </Column>
      </div>

      {excTxn && (
        <ExceptionDialog open={!!excTxn} onOpenChange={(o) => !o && setExcTxn(null)} txn={excTxn}
          options={FD_EXCEPTIONS} onDone={reload} />
      )}

      <AlertDialog open={!!deliverTxn} onOpenChange={(o) => !o && setDeliverTxn(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Confirm Delivery</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm that the package has been delivered to the customer ({deliverTxn?.kin_number}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => act(deliverTxn.id, "deliver")} data-testid="confirm-deliver-btn">Confirm Delivered</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
